/**
 * INVOICE MOCK DATA
 *
 * Centralized mock data for invoices including list views and records.
 * Uses mockDataManager for localStorage persistence.
 */

import { createPersistedMockData } from '../mockDataManager';
import type { ListView, ListViewRecord } from '../../types/listView';
import type { Invoice } from '../../types/index';

/**
 * DEFAULT INVOICE LIST VIEWS
 */
export const defaultInvoiceListViews: ListView[] = [
  {
    id: 'lv_invoices_all',
    name: 'All Invoices',
    apiName: 'all_invoices',
    objectApiName: 'Invoice',
    description: 'All invoice records',
    isDefault: true,
    columns: [
      { fieldApiName: 'invoiceNumber', fieldLabel: 'Invoice Number', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'clientName', fieldLabel: 'Client', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'issueDate', fieldLabel: 'Issue Date', fieldType: 'date', isVisible: true, order: 2 },
      { fieldApiName: 'dueDate', fieldLabel: 'Due Date', fieldType: 'date', isVisible: true, order: 3 },
      { fieldApiName: 'totalAmount', fieldLabel: 'Total Amount', fieldType: 'number', isVisible: true, order: 4 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 5 },
      { fieldApiName: 'paymentLink', fieldLabel: 'Payment Link', fieldType: 'text', isVisible: true, order: 6 },
    ],
    createdBy: 'System',
    createdDate: '2024-01-01T00:00:00Z'
  },
  {
    id: 'lv_invoices_unpaid',
    name: 'Unpaid Invoices',
    apiName: 'unpaid_invoices',
    objectApiName: 'Invoice',
    description: 'Invoices that are not yet paid',
    isDefault: false,
    columns: [
      { fieldApiName: 'invoiceNumber', fieldLabel: 'Invoice Number', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'clientName', fieldLabel: 'Client', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'dueDate', fieldLabel: 'Due Date', fieldType: 'date', isVisible: true, order: 2 },
      { fieldApiName: 'totalAmount', fieldLabel: 'Total Amount', fieldType: 'number', isVisible: true, order: 3 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 4 },
    ],
    filters: [
      { fieldApiName: 'status', operator: 'notEquals', value: 'paid' }
    ],
    createdBy: 'Admin',
    createdDate: '2024-02-15T10:30:00Z'
  },
  {
    id: 'lv_invoices_overdue',
    name: 'Overdue Invoices',
    apiName: 'overdue_invoices',
    objectApiName: 'Invoice',
    description: 'Invoices past due date',
    isDefault: false,
    columns: [
      { fieldApiName: 'invoiceNumber', fieldLabel: 'Invoice Number', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'clientName', fieldLabel: 'Client', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'dueDate', fieldLabel: 'Due Date', fieldType: 'date', isVisible: true, order: 2 },
      { fieldApiName: 'totalAmount', fieldLabel: 'Total Amount', fieldType: 'number', isVisible: true, order: 3 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 4 },
    ],
    filters: [
      { fieldApiName: 'status', operator: 'equals', value: 'overdue' }
    ],
    createdBy: 'Admin',
    createdDate: '2024-03-10T14:00:00Z'
  },
  {
    id: 'lv_invoices_this_month',
    name: 'This Month',
    apiName: 'invoices_this_month',
    objectApiName: 'Invoice',
    description: 'Invoices issued this month',
    isDefault: false,
    columns: [
      { fieldApiName: 'invoiceNumber', fieldLabel: 'Invoice Number', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'clientName', fieldLabel: 'Client', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'issueDate', fieldLabel: 'Issue Date', fieldType: 'date', isVisible: true, order: 2 },
      { fieldApiName: 'totalAmount', fieldLabel: 'Total Amount', fieldType: 'number', isVisible: true, order: 3 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 4 },
    ],
    createdBy: 'Admin',
    createdDate: '2024-04-01T10:00:00Z'
  }
];

/**
 * DEFAULT INVOICE RECORDS
 */
export const defaultInvoiceRecords: Invoice[] = [
  {
    id: 'inv_001',
    invoiceNumber: 'INV-00001',
    clientId: 'client_001',
    clientName: 'Tech Innovations Inc',
    status: 'paid',
    issueDate: '2024-12-01',
    dueDate: '2024-12-31',
    paidDate: '2024-12-15',
    subtotal: 248.00,
    taxAmount: 0,
    totalAmount: 248.00,
    currency: 'USD',
    paymentLink: 'https://pay.regalcredit.com/invoice/inv_001',
    notes: 'Monthly subscription services',
    lineItems: [
      {
        id: 'li_001',
        productId: 'prod_001',
        productName: 'Credit Repair',
        productCode: 'CR-001',
        quantity: 1,
        unitPrice: 99.00,
        lineTotal: 99.00
      },
      {
        id: 'li_002',
        productId: 'prod_002',
        productName: 'CyberSweep',
        productCode: 'CS-001',
        quantity: 1,
        unitPrice: 149.00,
        lineTotal: 149.00
      }
    ],
    createdDate: '2024-12-01T09:00:00Z',
    createdBy: 'admin',
    lastModifiedDate: '2024-12-15T14:30:00Z'
  },
  {
    id: 'inv_002',
    invoiceNumber: 'INV-00002',
    clientId: 'client_002',
    clientName: 'Global Manufacturing Corp',
    status: 'sent',
    issueDate: '2025-01-01',
    dueDate: '2025-01-31',
    subtotal: 297.00,
    taxAmount: 0,
    totalAmount: 297.00,
    currency: 'USD',
    paymentLink: 'https://pay.regalcredit.com/invoice/inv_002',
    notes: 'Enterprise pricing applied',
    lineItems: [
      {
        id: 'li_003',
        productId: 'prod_001',
        productName: 'Credit Repair',
        productCode: 'CR-001',
        quantity: 1,
        unitPrice: 79.00,
        lineTotal: 79.00
      },
      {
        id: 'li_004',
        productId: 'prod_002',
        productName: 'CyberSweep',
        productCode: 'CS-001',
        quantity: 1,
        unitPrice: 119.00,
        lineTotal: 119.00
      },
      {
        id: 'li_005',
        productId: 'prod_008',
        productName: 'Premium Support Package',
        productCode: 'PSP-001',
        quantity: 1,
        unitPrice: 49.99,
        discount: 10,
        lineTotal: 44.99
      }
    ],
    createdDate: '2025-01-01T10:00:00Z',
    createdBy: 'admin',
    lastModifiedDate: '2025-01-01T10:00:00Z'
  },
  {
    id: 'inv_003',
    invoiceNumber: 'INV-00003',
    clientId: 'client_003',
    clientName: 'Retail Solutions LLC',
    status: 'overdue',
    issueDate: '2024-12-15',
    dueDate: '2025-01-15',
    subtotal: 158.00,
    taxAmount: 0,
    totalAmount: 158.00,
    currency: 'USD',
    notes: 'Payment reminder sent',
    lineItems: [
      {
        id: 'li_006',
        productId: 'prod_003',
        productName: 'Credit Block',
        productCode: 'CB-001',
        quantity: 1,
        unitPrice: 59.00,
        lineTotal: 59.00
      },
      {
        id: 'li_007',
        productId: 'prod_004',
        productName: 'Credit Development',
        productCode: 'CD-001',
        quantity: 1,
        unitPrice: 89.00,
        lineTotal: 89.00
      }
    ],
    createdDate: '2024-12-15T11:00:00Z',
    createdBy: 'admin',
    lastModifiedDate: '2025-01-16T09:00:00Z'
  },
  {
    id: 'inv_004',
    invoiceNumber: 'INV-00004',
    clientId: 'client_004',
    clientName: 'Financial Services Group',
    status: 'draft',
    issueDate: '2025-01-20',
    dueDate: '2025-02-20',
    subtotal: 35.00,
    taxAmount: 0,
    totalAmount: 35.00,
    currency: 'USD',
    notes: 'One-time credit report purchase',
    lineItems: [
      {
        id: 'li_008',
        productId: 'prod_006',
        productName: 'Tri-Merge Credit Report',
        productCode: 'CRPT-003',
        quantity: 1,
        unitPrice: 35.00,
        lineTotal: 35.00
      }
    ],
    createdDate: '2025-01-20T14:00:00Z',
    createdBy: 'admin',
    lastModifiedDate: '2025-01-20T14:00:00Z'
  },
  {
    id: 'inv_005',
    invoiceNumber: 'INV-00005',
    clientId: 'client_005',
    clientName: 'Healthcare Innovations',
    status: 'paid',
    issueDate: '2024-11-01',
    dueDate: '2024-11-30',
    paidDate: '2024-11-15',
    subtotal: 228.99,
    taxAmount: 0,
    totalAmount: 228.99,
    currency: 'USD',
    paymentLink: 'https://pay.regalcredit.com/invoice/inv_005',
    lineItems: [
      {
        id: 'li_009',
        productId: 'prod_001',
        productName: 'Credit Repair',
        productCode: 'CR-001',
        quantity: 1,
        unitPrice: 99.00,
        lineTotal: 99.00
      },
      {
        id: 'li_010',
        productId: 'prod_007',
        productName: 'Credit Monitoring Add-On',
        productCode: 'CM-ADDON',
        quantity: 1,
        unitPrice: 19.99,
        lineTotal: 19.99
      },
      {
        id: 'li_011',
        productId: 'prod_008',
        productName: 'Premium Support Package',
        productCode: 'PSP-001',
        quantity: 1,
        unitPrice: 49.99,
        lineTotal: 49.99
      }
    ],
    createdDate: '2024-11-01T09:00:00Z',
    createdBy: 'admin',
    lastModifiedDate: '2024-11-15T10:30:00Z'
  },
  {
    id: 'inv_006',
    invoiceNumber: 'INV-00006',
    clientId: 'client_003',
    clientName: 'Retail Solutions LLC',
    status: 'sent',
    issueDate: '2025-01-10',
    dueDate: '2025-02-10',
    subtotal: 199.00,
    taxAmount: 0,
    totalAmount: 199.00,
    currency: 'USD',
    lineItems: [
      { id: 'li_012', productId: 'prod_002', productName: 'CyberSweep', productCode: 'CS-001', quantity: 1, unitPrice: 149.00, lineTotal: 149.00 },
      { id: 'li_013', productId: 'prod_008', productName: 'Premium Support Package', productCode: 'PSP-001', quantity: 1, unitPrice: 50.00, lineTotal: 50.00 }
    ],
    createdDate: '2025-01-10T09:00:00Z',
    createdBy: 'admin',
    lastModifiedDate: '2025-01-10T09:00:00Z'
  },
  {
    id: 'inv_007',
    invoiceNumber: 'INV-00007',
    clientId: 'client_001',
    clientName: 'Tech Innovations Inc',
    status: 'draft',
    issueDate: '2025-01-15',
    dueDate: '2025-02-15',
    subtotal: 448.00,
    taxAmount: 0,
    totalAmount: 448.00,
    currency: 'USD',
    lineItems: [
      { id: 'li_014', productId: 'prod_001', productName: 'Credit Repair', productCode: 'CR-001', quantity: 2, unitPrice: 99.00, lineTotal: 198.00 },
      { id: 'li_015', productId: 'prod_002', productName: 'CyberSweep', productCode: 'CS-001', quantity: 1, unitPrice: 149.00, lineTotal: 149.00 },
      { id: 'li_016', productId: 'prod_008', productName: 'Premium Support Package', productCode: 'PSP-001', quantity: 2, unitPrice: 50.50, lineTotal: 101.00 }
    ],
    createdDate: '2025-01-15T11:00:00Z',
    createdBy: 'admin',
    lastModifiedDate: '2025-01-15T11:00:00Z'
  },
  {
    id: 'inv_008',
    invoiceNumber: 'INV-00008',
    clientId: 'client_004',
    clientName: 'Summit Consulting Group',
    status: 'paid',
    issueDate: '2024-12-20',
    dueDate: '2025-01-20',
    paidDate: '2025-01-05',
    subtotal: 319.00,
    taxAmount: 0,
    totalAmount: 319.00,
    currency: 'USD',
    lineItems: [
      { id: 'li_017', productId: 'prod_001', productName: 'Credit Repair', productCode: 'CR-001', quantity: 1, unitPrice: 99.00, lineTotal: 99.00 },
      { id: 'li_018', productId: 'prod_002', productName: 'CyberSweep', productCode: 'CS-001', quantity: 1, unitPrice: 149.00, lineTotal: 149.00 },
      { id: 'li_019', productId: 'prod_007', productName: 'Credit Monitoring Add-On', productCode: 'CM-ADDON', quantity: 1, unitPrice: 19.99, lineTotal: 19.99 },
      { id: 'li_020', productId: 'prod_008', productName: 'Premium Support Package', productCode: 'PSP-001', quantity: 1, unitPrice: 51.01, lineTotal: 51.01 }
    ],
    createdDate: '2024-12-20T10:00:00Z',
    createdBy: 'admin',
    lastModifiedDate: '2025-01-05T14:00:00Z'
  },
  {
    id: 'inv_009',
    invoiceNumber: 'INV-00009',
    clientId: 'client_002',
    clientName: 'Global Manufacturing Corp',
    status: 'overdue',
    issueDate: '2024-11-20',
    dueDate: '2024-12-20',
    subtotal: 597.00,
    taxAmount: 0,
    totalAmount: 597.00,
    currency: 'USD',
    notes: 'Q4 subscription renewal',
    lineItems: [
      { id: 'li_021', productId: 'prod_001', productName: 'Credit Repair', productCode: 'CR-001', quantity: 2, unitPrice: 79.00, lineTotal: 158.00 },
      { id: 'li_022', productId: 'prod_002', productName: 'CyberSweep', productCode: 'CS-001', quantity: 2, unitPrice: 119.00, lineTotal: 238.00 },
      { id: 'li_023', productId: 'prod_008', productName: 'Premium Support Package', productCode: 'PSP-001', quantity: 2, unitPrice: 49.99, lineTotal: 99.98 },
      { id: 'li_024', productId: 'prod_007', productName: 'Credit Monitoring Add-On', productCode: 'CM-ADDON', quantity: 1, unitPrice: 19.99, lineTotal: 19.99 }
    ],
    createdDate: '2024-11-20T08:00:00Z',
    createdBy: 'admin',
    lastModifiedDate: '2024-11-20T08:00:00Z'
  }
];

/**
 * PERSISTED STORAGE
 */
export const persistedInvoiceListViews = createPersistedMockData(
  'crm_invoice_list_views',
  defaultInvoiceListViews
);

export const persistedInvoiceRecords = createPersistedMockData(
  'crm_invoice_records',
  defaultInvoiceRecords
);

/**
 * BACKWARD COMPATIBILITY
 */
export const mockInvoiceListViews = defaultInvoiceListViews;
export const mockInvoiceRecords = defaultInvoiceRecords;
